import "./edit-transaction.scss";
import { FaChevronLeft } from "react-icons/fa6";
import { FormProvider } from "react-hook-form";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";

import SkeletonList from "@shared/ui/skeleton-list/skeleton-list";
import ErrorSection from "@shared/ui/error-section/error-section";
import HeaderContent from "@shared/ui/header-content/header-content";
import HeaderButton from "@shared/ui/header-button/header-button";

import { useEditTransaction } from "./edit-transaction.hook";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import TextBlock from "@shared/ui/text-block/text-block";
import { currencyFormat } from "@shared/utility/formats/currency-format";
import { Divider } from "primereact/divider";
import DotLabel from "@shared/ui/dot-label/dot-label";
import StatusTag from "@core/ui/status-tag/status-tag";
import TransactionQr from "../transaction-qr/transaction-qr";
import ServiceInfo from "@core/ui/service-info/service-info";
import { Accordion, AccordionTab } from "primereact/accordion";
import ParentInfo from "@core/ui/parent-info/parent-info";
import { Button } from "primereact/button";
import { Sidebar } from "primereact/sidebar";
import EditTransactionService from "@domain/transaction-service/edit-transaction-service/edit-transaction-service";
import ChildInfo from "@core/ui/child-info/child-info";
import { Tag } from "primereact/tag";
import { useState } from "react";
import { NewTransactionService } from "@domain/transaction-service/new-transaction-service";
import PrintWaiver from "@domain/transaction-waiver/print-waiver/print-waiver";
import QRCode from "react-qr-code";
import { useTransactionHelperContext } from "../transaction-helper/transaction-helper.context";
import { useTransactionUtil } from "@core/utility/transaction.helper";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { format } from "date-fns";
import { ActionEnum } from "@factory/model/actions.enum";

export function EditTransaction() {
  const {
    printQrRef,
    printWaiverRef,
    transactionData,
    isLoading,
    transactionError,
    formMethod,
    servicePanel,
    servicePanelData,
    qrData,
    selectedWaiverPreview,
    signaturePreview,
    parentQr,
    getActions,
    handleQrData,
    setServicePanel,
    handleBack,
    handleAction,
    handleServiceChange,
    handleServiceChangeSuucess,
    navigate,
    checkoutTransaction,
    mapDeleteTransaction,
  } = useEditTransaction();
  const { getServiceStart, getEndDateTime } = useTransactionUtil();
  const { combinedServices } = useTransactionHelperContext();
  const { formatDateTimeLocal } = useDateTimeContext();
  const services = combinedServices(transactionData?.service || []);
  const [addonSidebarVisible, setAddonSidebarVisible] = useState(false);
  const [addServiceSidebarVisible, setAddServiceSidebarVisible] =
    useState(false);
  const [finishSessionDialogVisible, setFinishSessionDialogVisible] =
    useState(false);

  // Determine if there is a guardian recorded on this booking.
  const firstService = (services[0] as any) || {};
  const hasGuardian = !!firstService.guardian;

  const parentName = `${transactionData?.parent_first_name ?? ""} ${transactionData?.parent_last_name ?? ""}`.trim();
  const parentMobile = transactionData?.mobile_number ?? "";
  
  const guardianName = hasGuardian
    ? `${firstService.guardian_first_name ?? ""} ${firstService.guardian_last_name ?? ""}`.trim()
    : parentName;
  const guardianMobile = hasGuardian
    ? (firstService.guardian_mobile_number ?? "")
    : parentMobile;

  const isParentAlsoGuardian = !hasGuardian || (
    firstService.guardian_first_name === transactionData?.parent_first_name &&
    firstService.guardian_last_name === transactionData?.parent_last_name
  );

  const guardianQr = hasGuardian
    ? `${transactionData?.code}:parent:${firstService.guardian}`
    : parentQr;

  const getDayType = (dateStr?: string) => {
    try {
      if (!dateStr) {
        return "";
      }
      const parts = dateStr.split("-");
      if (parts.length === 3) {
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // 0-indexed
        const day = parseInt(parts[2], 10);
        const date = new Date(year, month, day);
        const dayOfWeek = date.getDay();
        return dayOfWeek === 0 || dayOfWeek === 6 ? "Weekend" : "Weekday";
      }
      return "";
    } catch {
      return "";
    }
  };

  // HTML
  const printSection = (
    <div style={{ display: "none" }}>
      <div ref={printWaiverRef}>
        {selectedWaiverPreview ? (
          <PrintWaiver
            waiver={selectedWaiverPreview}
            signature={signaturePreview}
          />
        ) : null}
      </div>
    </div>
  );
  const printQrSection = (
    <div style={{ display: "none" }}>
      <div ref={printQrRef}>{qrData && <TransactionQr model={qrData} />}</div>
    </div>
  );
  const title = "Edit Booking";
  const displayLoading = (
    <div className="card">
      <SkeletonList count={4} />
    </div>
  );
  const displayError = (
    <div className="card">
      <ErrorSection
        title="No Data"
        message="No data found in selected record. Please try again."
      />
    </div>
  );
  const changeServiceDialog = (
    <Dialog
      header="Change Service"
      visible={servicePanel}
      onHide={() => setServicePanel(false)}
      style={{ width: "50vw" }}
      draggable={false}
      resizable={false}
      modal
    >
      {servicePanelData ? (
        <section>
          <ChildInfo
            className="mb-10"
            name={`${servicePanelData.model?.first_name} ${servicePanelData.model?.last_name}`}
          />
          <EditTransactionService
            model={servicePanelData.service}
            onUpdateSuccess={handleServiceChangeSuucess}
          />
        </section>
      ) : (
        <span>No Data Available</span>
      )}
    </Dialog>
  );
  const mainContent = (
    <div className="space-y-8 flex flex-col justify-between">
        <div className="space-y-8">
          {/* Children Table */}
          <div>
            <h4 className="text-sm font-bold text-gray-900 tracking-wider mb-3 block md:hidden">
              CHILD
            </h4>
            {/* Desktop Table */}
            <div className="hidden md:block">
              <table className="w-full border-collapse text-left">
                <thead>
                  <tr>
                    <th className="pb-3 text-sm font-bold text-gray-900 tracking-wider w-3/12">
                      CHILD
                    </th>
                    <th className="pb-3 text-sm font-bold text-gray-900 tracking-wider w-3/12">
                      TYPE
                    </th>
                    <th className="pb-3 text-sm font-bold text-gray-900 tracking-wider w-2/12">
                      TIME START
                    </th>
                    <th className="pb-3 text-sm font-bold text-gray-900 tracking-wider w-2/12">
                      TIME END
                    </th>
                    <th className="pb-3 text-sm font-bold text-gray-900 tracking-wider w-2/12 text-right">
                      ACTIONS
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {services.map((item) => {
                    const startTime = formatDateTimeLocal(getServiceStart(item));
                    const end = getEndDateTime(item);
                    const endTime = item.is_open_time
                      ? "Open Time"
                      : end
                      ? formatDateTimeLocal(end)
                      : "";
                    const dayType = getDayType(item.date);
                    const typeDisplay = dayType ? `${item.service_name} ${dayType}` : item.service_name;

                    return (
                      <tr key={item.member} className="align-middle">
                        <td className="py-2 text-base text-gray-900 font-medium">
                          <span className="flex items-center gap-2">
                            <span>{item.first_name} {item.last_name}</span>
                            {!!item.is_checkout && (
                              <span className="bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded font-semibold">
                                Checkout
                              </span>
                            )}
                          </span>
                        </td>
                        <td className="py-2 text-base text-gray-700">
                          {typeDisplay}
                        </td>
                        <td className="py-2 text-base text-gray-700">
                          {startTime}
                        </td>
                        <td className="py-2 text-base text-gray-700">
                          {endTime}
                        </td>
                        <td className="py-2 text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              label="Change"
                              size="small"
                              outlined
                              severity="secondary"
                              onClick={() => handleServiceChange(item)}
                              className="p-button-sm text-xs font-semibold whitespace-nowrap"
                            />
                            <Button
                              label="Print QR"
                              size="small"
                              outlined
                              severity="secondary"
                              onClick={(e) => {
                                e.preventDefault();
                                handleQrData(
                                  item.code,
                                  `${item.first_name} ${item.last_name}`,
                                  false
                                );
                              }}
                              className="p-button-sm text-xs font-semibold whitespace-nowrap"
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="block md:hidden space-y-4">
              {services.map((item) => {
                const startTime = formatDateTimeLocal(getServiceStart(item));
                const end = getEndDateTime(item);
                const endTime = item.is_open_time
                  ? "Open Time"
                  : end
                  ? formatDateTimeLocal(end)
                  : "";
                const dayType = getDayType(item.date);
                const typeDisplay = dayType ? `${item.service_name} ${dayType}` : item.service_name;

                return (
                  <div key={item.member} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-gray-900 text-base">
                        {item.first_name} {item.last_name}
                      </span>
                      {!!item.is_checkout && (
                        <span className="bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded font-semibold">
                          Checkout
                        </span>
                      )}
                    </div>
                    <div className="border-t border-gray-100 my-2"></div>
                    <div className="grid grid-cols-1 gap-2 text-sm text-gray-600">
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-400 uppercase text-xs">Type</span>
                        <span>{typeDisplay}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-400 uppercase text-xs">Start</span>
                        <span>{startTime}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-400 uppercase text-xs">End</span>
                        <span>{endTime}</span>
                      </div>
                    </div>
                    <div className="border-t border-gray-100 my-2"></div>
                    <div className="flex gap-2 justify-end">
                      <Button
                        label="Change"
                        size="small"
                        outlined
                        severity="secondary"
                        onClick={() => handleServiceChange(item)}
                        className="p-button-sm text-xs font-semibold"
                      />
                      <Button
                        label="Print QR"
                        size="small"
                        outlined
                        severity="secondary"
                        onClick={(e) => {
                          e.preventDefault();
                          handleQrData(
                            item.code,
                            `${item.first_name} ${item.last_name}`,
                            false
                          );
                        }}
                        className="p-button-sm text-xs font-semibold"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Addon Table */}
          {transactionData?.addon && transactionData.addon.length > 0 && (
            <div className="mt-8">
              <h4 className="text-sm font-bold text-gray-900 tracking-wider mb-3">
                ADDON
              </h4>
              {/* Desktop Table */}
              <div className="hidden md:block">
                <table className="w-full border-collapse text-left">
                  <thead>
                    <tr>
                      <th className="pb-3 text-sm font-medium text-gray-500 tracking-wider w-1/4">
                        Name
                      </th>
                      <th className="pb-3 text-sm font-medium text-gray-500 tracking-wider w-1/4">
                        Quantity
                      </th>
                      <th className="pb-3 text-sm font-medium text-gray-500 tracking-wider w-1/4">
                        Amount
                      </th>
                      <th className="pb-3 text-sm font-medium text-gray-500 tracking-wider w-1/4">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactionData.addon.map((addonItem: any) => (
                      <tr key={addonItem.id || addonItem.item_name} className="align-middle">
                        <td className="py-2 text-base text-gray-900 font-medium">
                          {addonItem.item_name}
                        </td>
                        <td className="py-2 text-base text-gray-700">
                          {addonItem.quantity}
                        </td>
                        <td className="py-2 text-base text-gray-700">
                          {currencyFormat(addonItem.amount || 0)}
                        </td>
                        <td className="py-2 text-base text-gray-700 font-semibold text-gray-900">
                          {currencyFormat(addonItem.sub_total || 0)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="block md:hidden space-y-4">
                {transactionData.addon.map((addonItem: any) => (
                  <div key={addonItem.id || addonItem.item_name} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-gray-900">{addonItem.item_name}</span>
                      <span className="font-bold text-gray-900">{currencyFormat(addonItem.sub_total || 0)}</span>
                    </div>
                    <div className="border-t border-gray-100 my-2"></div>
                    <div className="grid grid-cols-1 gap-2 text-sm text-gray-600">
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-400 uppercase text-xs">Quantity</span>
                        <span>{addonItem.quantity}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-400 uppercase text-xs">Unit Price</span>
                        <span>{currencyFormat(addonItem.amount || 0)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Guardian Details */}
        <div className="flex flex-col md:flex-row gap-8 mt-12">
          <div className="flex-1">
            <h4 className="text-sm font-bold text-gray-900 tracking-wider mb-4">
              GUARDIAN DETAILS
            </h4>
            <div className="flex flex-col sm:flex-row items-center gap-6 bg-white p-6 border border-gray-200 rounded-lg shadow-sm">
              <div className="p-2 border border-gray-100 rounded bg-white shadow-sm flex items-center justify-center">
                <QRCode value={guardianQr} size={110} />
              </div>
              <div className="flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left">
                <div className="w-16 h-16 rounded-full border border-gray-300 flex items-center justify-center text-gray-400 bg-white flex-shrink-0">
                  <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <div className="flex flex-col">
                  <span className="text-xl font-bold text-gray-900 leading-none">
                    {guardianName}
                  </span>
                  <div className="border-t border-gray-300 my-2 w-24 mx-auto sm:mx-0"></div>
                  <span className="text-lg font-semibold text-gray-900 leading-none">
                    {guardianMobile}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div></div>
  );

  const finishSessionDialog = (
    <Dialog
      header="Finish Session"
      visible={finishSessionDialogVisible}
      onHide={() => setFinishSessionDialogVisible(false)}
      style={{ width: "350px" }}
      draggable={false}
      resizable={false}
      modal
    >
      <div className="flex flex-col gap-3 pt-2">
        <p className="text-gray-600 text-sm mb-2">
          Select which child to finish their playing session, or complete the entire booking.
        </p>

        {services.filter(item => !item.is_checkout).map((item) => (
          <Button
            key={item.member}
            label={`Finish ${item.first_name} ${item.last_name}`}
            icon="pi pi-user"
            className="p-button-outlined p-button-secondary w-full text-left justify-start"
            onClick={() => {
              setFinishSessionDialogVisible(false);
              confirmDialog({
                message: `Are you sure you want to finish the session for ${item.first_name} ${item.last_name}?`,
                header: "Finish Child Session",
                icon: "pi pi-info-circle",
                acceptClassName: "p-button-danger",
                accept() {
                  checkoutTransaction(mapDeleteTransaction(item.code));
                },
              });
            }}
          />
        ))}

        {services.filter(item => !item.is_checkout).length > 0 && <Divider className="my-2" />}

        <Button
          label="Finish All Sessions"
          icon="pi pi-check-circle"
          className="p-button-success w-full"
          onClick={() => {
            setFinishSessionDialogVisible(false);
            handleAction({ value: ActionEnum.Complete } as any);
          }}
        />
      </div>
    </Dialog>
  );

  const addServiceDialog = (
    <Dialog
      header="Add Service"
      visible={addServiceSidebarVisible}
      onHide={() => setAddServiceSidebarVisible(false)}
      style={{ width: "50vw" }}
      draggable={false}
      resizable={false}
      modal
    >
      {transactionData && (
        <NewTransactionService
          transactionData={transactionData} // // Pass full transaction data
          onServiceAdded={() => {
            setAddServiceSidebarVisible(false);
            // // The transaction data will automatically refresh due to query invalidation
          }}
        />
      )}
    </Dialog>
  );

  return (
    <div id="EditTransaction" className="transaction">
      <HeaderContent
        title={title}
        backIcon={<FaChevronLeft />}
        onBack={() => handleBack()}
      >
        {transactionData && (
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center w-full md:w-auto">
            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2 items-center">
              <Button
                label="Add Service"
                icon="pi pi-plus"
                outlined
                severity="secondary"
                disabled={!transactionData || transactionData.service?.length === 0}
                onClick={() => setAddServiceSidebarVisible(true)}
                className="p-button-sm font-semibold transaction-header-btn btn-add-service"
              />
              <Button
                label="Add Addon"
                icon="pi pi-plus"
                outlined
                severity="secondary"
                onClick={() => navigate("new-addon")}
                className="p-button-sm font-semibold transaction-header-btn btn-add-addon"
              />
              <Button
                label="Finish Session"
                icon="pi pi-check"
                outlined
                severity="success"
                onClick={() => setFinishSessionDialogVisible(true)}
                className="p-button-sm font-semibold transaction-header-btn btn-finish-session"
              />
              <Button
                label="Pay Now"
                icon="pi pi-credit-card"
                severity="danger"
                disabled={transactionData.is_paid}
                onClick={() => handleAction({ value: ActionEnum.Pay } as any)}
                className="p-button-sm font-semibold transaction-header-btn btn-pay-now"
              />
              <Button
                label="Scan QR Code"
                icon="pi pi-qrcode"
                outlined
                severity="info"
                onClick={() => handleAction({ value: ActionEnum.ScanComplete } as any)}
                className="p-button-sm font-semibold transaction-header-btn btn-scan-qr"
              />
            </div>

            {/* Status indicator Card */}
            <div className="flex items-center border border-gray-200 rounded bg-white shadow-sm divide-x divide-gray-200 h-9">
              <div className="flex items-center gap-2 px-3 py-1 text-xs font-bold text-gray-900 justify-center">
                <span
                  className={`w-2 h-2 rounded-full ${
                    transactionData.is_paid ? "bg-green-600" : "bg-red-600"
                  }`}
                />
                <span>{transactionData.is_paid ? "PAID" : "UNPAID"}</span>
              </div>
              <div className="px-3 py-1 font-bold text-sm text-gray-900 text-center font-mono">
                {currencyFormat(transactionData.total || 0)}
              </div>
            </div>
          </div>
        )}
      </HeaderContent>

      {printSection}
      {printQrSection}
      {changeServiceDialog}
      {addServiceDialog}
      {finishSessionDialog}

      <section className="p-7">
        <ConfirmDialog />
        <FormProvider {...formMethod}>
          {isLoading && displayLoading}
          {transactionError && !isLoading && displayError}
          {!isLoading ? mainContent : null}
        </FormProvider>
      </section>
    </div>
  );
}

export default EditTransaction;
